import { ApolloQueryResult } from '@apollo/client';
import { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import React, { ReactElement } from 'react';
import { useStoryblok } from 'storyblok';

import { Layout } from '@/components';
import Footer from '@/components/global/Footer';
import { Column, Grid } from '@/components/global/Grid';
import Header from '@/components/global/Header';
import { FontStyle } from '@/components/global/PanelFont/types';
import BaseSeo from '@/components/seo/BaseSeo';
import ColophonStandard from '@/components/tools/ColophonStandard/ColophonStandard';
import { serverApolloClient } from '@/lib/auth';
import { VERSION } from '@/lib/constants';
import { isFamilyVariant, isVariableFont } from '@/lib/productHelpers';
import { contextToRegionQuery, DEFAULT_CHANNEL } from '@/lib/regions';
import { getStoryBlokProps } from '@/lib/storyblok/storyblokGetStaticProps';
import { getStoryBlokNavLinks } from '@/lib/storyblok/storyblokNavLinks';
import { formatAwsS3FontData, generateAwsFontDataPromise } from '@/lib/util';
import {
  ProductBySlugDocument,
  ProductBySlugQuery,
  ProductBySlugQueryVariables,
  ProductFontsDropdownDocument,
  ProductFontsDropdownQuery,
  ProductFontsDropdownQueryVariables
} from '@/saleor/api';

const ColophonStandardPage = ({
  products,
  fontFamilyData,
  story,
  preview,
  resolve_relations
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { content } = story;
  const { description, languages } = content;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const sbStory = preview ? useStoryblok(story, preview, resolve_relations) : story;
  const globalLinks = getStoryBlokNavLinks(story);

  const {
    content: { SEO = {} },
    name,
    full_slug
  } = sbStory;

  return (
    <>
      <BaseSeo
        title={SEO?.title}
        description={SEO?.description}
        ogDescription={SEO?.og_description}
        ogTitle={SEO?.og_title}
        image={SEO?.og_image}
        path={full_slug}
      />
      <Header navigation={globalLinks} pageName={name} headerTheme="header-white" hasBorder />
      <main id="main" className="page-theme-charcoal th-white-charcoal page-padding-top">
        <Grid>
          <Column sm={12} md={12} lg={12}>
            <ColophonStandard
              title={name}
              description={description}
              languages={languages.split('\n')}
              products={products}
              serverFontFamilyData={fontFamilyData}
            />
          </Column>
        </Grid>
      </main>
      <Footer theme="charcoal" navigation={globalLinks.footerLinks} />
    </>
  );
};

export const getStaticProps = async (context: GetStaticPropsContext) => {
  const preview = context.preview || false;
  const resolveRelations = 'PageColophonStandard.globalHeader,PageColophonStandard.globalFooter';

  const storyPromise = getStoryBlokProps({
    pageSlug: 'tools/colophon-standard',
    context,
    VERSION,
    resolveRelations
  });

  const pageData = await Promise.all([storyPromise]).then((values) => values);

  const story = pageData[0];

  if (story?.notFound) return story;

  const productsRes: ApolloQueryResult<ProductFontsDropdownQuery> = await serverApolloClient.query<
    ProductFontsDropdownQuery,
    ProductFontsDropdownQueryVariables
  >({
    query: ProductFontsDropdownDocument,
    variables: {
      filter: { categories: [process.env.NEXT_PUBLIC_TYPEFACE_PRODUCTS_CATEGORY] },
      channel: DEFAULT_CHANNEL.slug
    }
  });

  const products = productsRes?.data?.products?.edges.map((edge) => edge.node) || [];
  // Only return fonts with Std variants
  const productsStd = products.filter((product) => {
    const proVariantsOnly = product.metadata.find(({ key }) => key === 'proVariantsOnly'); // Metadata from saleor
    return !proVariantsOnly;
  });

  // Saleor data
  const responseProductFontFamily: ApolloQueryResult<ProductBySlugQuery> =
    await serverApolloClient.query<ProductBySlugQuery, ProductBySlugQueryVariables>({
      query: ProductBySlugDocument,
      variables: {
        slug: products?.[0].slug,
        ...contextToRegionQuery(context)
      }
    });

  const variantFontStyles = responseProductFontFamily.data.product.variants;

  // Construct promises for fetching wakamai fondue data from AWS
  const awsS3fontDataPromises = variantFontStyles
    .filter((fontStyle) => !isFamilyVariant(fontStyle))
    .filter((fontStyle) => !isVariableFont(fontStyle))
    .filter((fontStyle) => !fontStyle.sku.includes('-pro'))
    .map((fontStyle) => generateAwsFontDataPromise(fontStyle));

  const awsS3FontData = await Promise.all(awsS3fontDataPromises);
  const awsS3fontDataFormatted = formatAwsS3FontData(awsS3FontData);

  return {
    props: {
      products: productsStd,
      fontFamilyData: {
        [`${responseProductFontFamily.data.product.slug}`]: Object.values(
          awsS3fontDataFormatted
        ) as FontStyle[]
      },
      preview,
      story: story || null,
      resolve_relations: resolveRelations
    },
    revalidate: 60 * 60 // value in seconds, how often ISR will trigger on the server
  };
};

ColophonStandardPage.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};

export default ColophonStandardPage;
